import { z } from 'zod';

import { defineQuery } from '@nesso/common-contracts/define';

import { practiceIdSchema, practiceMemberIdSchema } from './ids';
import { PRACTICE_ACCESS_SOURCES } from './practice-access-rule.access-sources';
import {
    EHR_INSIGHTS_TYPE,
    ehrPlatformsSchema,
    GUIDANCE_PREFERENCE_TYPE,
    guidanceKeySchema,
    ONBOARDING_PREFERENCE_TYPE,
    onboardingStepsSchema,
    WELCOME_GUIDE_PREFERENCE_TYPE,
    WHY_INSIGHTS_TYPE,
    welcomeGuideTasksSchema,
} from './practice-member.models';

export const GET_PRACTICE_MEMBER_BY_ID_TYPE = 'practice-member/get-by-id';
export const GET_PRACTICE_MEMBER_INSIGHTS_TYPE = 'practice-member/get-insights';
export const GET_PRACTICE_MEMBER_PREFERENCES_TYPE = 'practice-member/get-preferences';
export const GET_PRACTICE_MEMBER_COMMUNICATION_DATA_TYPE = 'practice-member/get-communication-data';
export const GET_PRACTICE_MEMBER_LICENSES_TYPE = 'practice-member/get-licenses';
export const GET_PRACTICE_MEMBER_NAMES_TYPE = 'practice-member/get-names';

export const practiceMemberSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    legalName: z.string().nullable(),
    preferredFirstName: z.string().nullable(),
    title: z.string().nullable(),
    npi: z.string().nullable(),
    accessSources: z.array(z.enum(PRACTICE_ACCESS_SOURCES)).default([]),
});

export type PracticeMember = z.input<typeof practiceMemberSchema>;

export const getPracticeMemberById = defineQuery(
    GET_PRACTICE_MEMBER_BY_ID_TYPE,
    z.object({
        id: practiceMemberIdSchema,
        accessSources: z
            .string()
            .transform((raw, ctx) => {
                try {
                    return JSON.parse(raw);
                } catch {
                    ctx.addIssue({ code: 'custom', message: 'accessSources must be valid JSON' });
                    return z.NEVER;
                }
            })
            .pipe(z.array(z.enum(PRACTICE_ACCESS_SOURCES)))
            .optional(),
    }),
    practiceMemberSchema,
);

export const practiceMemberInsightsSchema = z.object({
    [WHY_INSIGHTS_TYPE]: z
        .object({
            customValue: z.string().optional(),
        })
        .optional(),
    [EHR_INSIGHTS_TYPE]: z
        .object({
            selection: ehrPlatformsSchema,
            customValue: z.string().optional(),
        })
        .optional(),
});

export type PracticeMemberInsights = z.input<typeof practiceMemberInsightsSchema>;

export const getPracticeMemberInsights = defineQuery(
    GET_PRACTICE_MEMBER_INSIGHTS_TYPE,
    z.object({
        practiceId: practiceIdSchema,
        practiceMemberId: practiceMemberIdSchema,
    }),
    practiceMemberInsightsSchema,
);

export const practiceMemberPreferencesSchema = z.object({
    [WELCOME_GUIDE_PREFERENCE_TYPE]: z
        .object({
            completedTasks: z.array(welcomeGuideTasksSchema),
            sampleSessionId: z.uuid().optional(),
            sampleClientId: z.uuid().optional(),
        })
        .optional(),
    [ONBOARDING_PREFERENCE_TYPE]: z
        .object({
            step: onboardingStepsSchema,
        })
        .optional(),
    [GUIDANCE_PREFERENCE_TYPE]: z
        .object({
            keys: z.array(guidanceKeySchema),
        })
        .optional(),
});

export type PracticeMemberPreferences = z.input<typeof practiceMemberPreferencesSchema>;

export const getPracticeMemberPreferences = defineQuery(
    GET_PRACTICE_MEMBER_PREFERENCES_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema,
    }),
    practiceMemberPreferencesSchema,
);

export const practiceMemberContactSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    preferredFirstName: z.string().nullable(),
});

export type PracticeMemberContact = z.input<typeof practiceMemberContactSchema>;

export const getPracticeMemberCommunicationData = defineQuery(
    GET_PRACTICE_MEMBER_COMMUNICATION_DATA_TYPE,
    z.object({
        ids: z.array(practiceMemberIdSchema),
    }),
    z.array(practiceMemberContactSchema),
);

// `type`, `state` and `taxonomyCode` are plain strings here, not the enums the write side
// uses: a state board or the NUCC can retire a key a therapist still holds, and because the
// output is an array, one enum-validated row would fail the whole response rather than degrade
// a single line of it.
export const licenseSchema = z.object({
    id: z.uuid(),
    type: z.string(),
    number: z.string(),
    state: z.string(),
    taxonomyCode: z.string().nullable(),
});

export type License = z.input<typeof licenseSchema>;

export const getPracticeMemberLicenses = defineQuery(
    GET_PRACTICE_MEMBER_LICENSES_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema,
    }),
    z.array(licenseSchema),
);

export const practiceMemberNamesSchema = z.object({
    legalName: z.string().nullable(),
    preferredFirstName: z.string().nullable(),
});

export type PracticeMemberNames = z.input<typeof practiceMemberNamesSchema>;

export const getPracticeMemberNames = defineQuery(
    GET_PRACTICE_MEMBER_NAMES_TYPE,
    z.object({ id: practiceMemberIdSchema }),
    practiceMemberNamesSchema.nullable(),
);
